import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Download, Search } from 'lucide-react';
import { toCsvContent } from '@/lib/csv';
import { downloadCsvFile } from '@/lib/download';
import { cn } from '@/lib/utils';
import type { ValidationError } from './useBulkStockUpload';
import { importRowReason } from '../../utils/supplyAliasCopy';

/**
 * THE shared "these rows were refused, and why" surface for every CSV import in
 * the inventory app: bulk stock upload, bulk stocktake upload, the purchase-order
 * CSV wizard and the Stock Hold-Back import (BLU-879 review, which is what
 * generalised it — the hold-back dialog had hand-rolled a fifth copy).
 *
 * Contract, in the order it matters:
 * - **Rejections are never silently dropped.** An import that discards a third
 *   of a file while reporting success is indistinguishable from one that
 *   worked, and the operator finds out at the next push.
 * - **Every rejection carries its SOURCE ROW.** The fix happens in a
 *   spreadsheet, and "row 214" is the only thing that gets them there.
 * - **The whole set is downloadable**, always the FULL set and never the
 *   filtered view: the download is a work list, not a screenshot. It goes
 *   through `downloadCsvFile`, so the UTF-8 BOM that stops Excel on Windows
 *   mangling Chinese product types is decided in exactly one place.
 * - **Search filters the table only.** The count in the caller's heading and
 *   the download both describe the unfiltered set.
 *
 * COLUMNS: the default four (Row / SKU / Qty / Error) match `ValidationError`,
 * so the four original callers pass `errors` alone and change nothing. A caller
 * with a different row shape passes `columns`, and the prop union below makes
 * that non-optional for them rather than letting a mismatched shape render four
 * empty columns.
 */

export interface ErrorTableColumn<T> {
  /** React key + CSV column identity. */
  key: string;
  /** Already-translated header label. */
  header: string;
  /** Cell content. May be a node; `csv` below carries the flat text. */
  cell: (row: T) => ReactNode;
  /** Flat value for the download. Defaults to the empty string. */
  csv?: (row: T) => string | number;
  /** Text the search box matches against. Omit to exclude from search. */
  search?: (row: T) => string;
  /** Applied to both `th` and `td`, e.g. a width or an alignment. */
  className?: string;
  /** Applied to `td` only, e.g. `text-destructive` on the reason column. */
  cellClassName?: string;
}

interface ErrorTableBaseProps {
  maxHeight?: string;
  /** Download filename. Date-stamp it when the caller can. */
  filename?: string;
  /** Wire to the caller's heading id so the table is announced with its count. */
  labelledById?: string;
  /** Render at most this many matching rows; search and the download still see every row. */
  maxRows?: number;
  /**
   * Placeholder and label of the search box. Defaults to the "SKU or error"
   * wording; a caller whose rows are not errors (a list of price changes)
   * says what its rows are.
   */
  searchPlaceholder?: string;
  /**
   * Extra classes on the `<table>`, e.g. `table-fixed` plus percentage column
   * widths so long cells wrap inside a narrow box instead of widening it.
   */
  tableClassName?: string;
}

/**
 * `columns` is optional ONLY when the rows are `ValidationError`s. The union is
 * the whole point: it is what stops a caller with a bespoke row type getting a
 * table of four blank columns at runtime.
 */
type ErrorTableProps<T> = ErrorTableBaseProps &
  (
    | { errors: readonly ValidationError[]; columns?: undefined }
    | { errors: readonly T[]; columns: readonly ErrorTableColumn<T>[] }
  ) & {
    /**
     * Stable `<tr key>` per row. Omit for `ValidationError` rows, where the
     * index is already unique; a caller with a row identity (e.g. a cell id)
     * should pass one so a re-sort or a mid-list edit doesn't reuse a DOM node
     * across two different rows.
     */
    rowKey?: (row: T, index: number) => string;
  };

const EMPTY_VALUE = '-';

function ErrorTable<T = ValidationError>(props: ErrorTableProps<T>) {
  const {
    maxHeight = '300px',
    filename = 'failed_rows.csv',
    labelledById,
    rowKey,
    maxRows,
    searchPlaceholder,
    tableClassName,
  } = props;
  const { t } = useTranslation('inventory');
  const placeholder = searchPlaceholder ?? t('errorTable.filterPlaceholder');
  const [search, setSearch] = useState('');

  /**
   * The default columns close over `t`, so they are built per render rather
   * than hoisted: a module-level table would freeze the labels at whichever
   * language happened to load first.
   */
  const defaultColumns = useMemo<ErrorTableColumn<ValidationError>[]>(
    () => [
      {
        key: 'row',
        header: t('errorTable.colRow'),
        className: 'w-16',
        cellClassName: 'tabular-nums text-muted-foreground',
        cell: (error) => error.row,
        csv: (error) => error.row,
      },
      {
        key: 'sku',
        header: t('errorTable.colSku'),
        className: 'w-40',
        cellClassName: 'font-mono',
        cell: (error) => error.sku,
        csv: (error) => error.sku,
        search: (error) => error.sku,
      },
      {
        key: 'quantity',
        header: t('errorTable.colQuantity'),
        className: 'w-16 text-right',
        cellClassName: 'tabular-nums text-muted-foreground',
        cell: (error) => (error.quantity != null ? error.quantity : EMPTY_VALUE),
        csv: (error) => error.quantity ?? '',
      },
      {
        key: 'reason',
        header: t('errorTable.colReason'),
        cellClassName: 'text-destructive',
        // A row with a reasonCode (Phase 92 `supply_alias`) shows the
        // translated reason; every other row shows the server's text as before.
        cell: (error) => importRowReason(t, error),
        csv: (error) => importRowReason(t, error),
        search: (error) => importRowReason(t, error),
      },
    ],
    [t]
  );

  // ONE cast, at the single point where the prop union collapses. The union
  // above has already guaranteed the pairing (ValidationError rows <-> default
  // columns), so this can only widen a type the compiler already checked.
  const columns = (props.columns ??
    defaultColumns) as unknown as readonly ErrorTableColumn<T>[];
  const errors = props.errors as readonly T[];

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return errors;
    return errors.filter((error) =>
      columns.some((column) => column.search?.(error).toLowerCase().includes(query))
    );
  }, [errors, columns, search]);

  const handleExportCsv = () => {
    const header = columns.map((column) => column.header);
    // The FULL set, not `filtered`: this file is the operator's work list.
    const rows = errors.map((error) => columns.map((column) => column.csv?.(error) ?? ''));
    downloadCsvFile(toCsvContent([header, ...rows]), filename);
  };

  return (
    <div className="space-y-2">
      {/* Search + Export */}
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            placeholder={placeholder}
            aria-label={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
        <Button variant="outline" size="sm" onClick={handleExportCsv} className="shrink-0">
          <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {t('errorTable.export')}
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-y-auto rounded-md border" style={{ maxHeight }}>
        <table className={cn('w-full text-sm', tableClassName)} aria-labelledby={labelledById}>
          <thead className="sticky top-0 z-10 bg-muted">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    'px-3 py-1.5 text-left text-xs font-medium text-muted-foreground',
                    column.className
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.slice(0, maxRows).map((error, index) => (
              <tr key={rowKey ? rowKey(error, index) : index} className="hover:bg-muted/50">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn('px-3 py-1.5 text-xs', column.className, column.cellClassName)}
                  >
                    {column.cell(error)}
                  </td>
                ))}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-4 text-center text-xs text-muted-foreground"
                >
                  {search ? t('errorTable.noMatches') : t('errorTable.noErrors')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// A function DECLARATION, exported as-is: typing it `React.FC` would erase the
// generic and every caller would collapse back to `ValidationError`.
export default ErrorTable;
